import type { AgentSatisfaction } from "@tdelivr/api-client/generated/support/support-case.js";
import type { WorkforceMember } from "@tdelivr/api-client/generated/identity/workforce-identity.js";

import type { StateClassKey } from "@tdelivr/design-tokens";

import type { GridOrder } from "./ui/DataGrid.tsx";

import { absent, field, type RecordDetail } from "./record-detail.ts";

/**
 * What the satisfaction board makes of SUP-01's per-agent projection, away from any rendering.
 *
 * Built to `20 WP19 Support Console.dc.html`, page `csat`:
 *
 * ```
 * csat:['Satisfaction','SUP-05','How customers rated their support, and cases reopened.',
 *   [['CSAT this week','4.6 / 5','done',''],['Responses','1,204','done',''],
 *    ['Reopened','3.1%','caution',''],['Median first reply','4 min','done','']],
 *   ['Agent','Cases','CSAT'],
 *   [['J. Wekesa','142','4.8','done','top'],
 *    ['P. Mutiso','118','4.5','done',''],
 *    ['R. Adhiambo','96','4.1','caution','coaching']],
 *   'info','Ratings are collected after a case closes, once per case.']
 * ```
 *
 * ## The recorded blocker was right, and ADR 0088 is why it was re-asked anyway
 *
 * It read: _"Nothing on the platform models support satisfaction: no rating, no response, no reopen
 * count. SUP-05, which `SCREEN_SPEC.md` cites, is the support escalation service — it holds
 * policies, timers and escalations, and every one of its operations is `/internal/v1`."_
 *
 * **The SUP-05 half is exactly right**, which is what ADR 0088 warned about on this very screen: the
 * replies board's note had been right about SUP-04 and wrong about everything else, because nobody
 * had asked whether a *different* service held the thing. So that question was asked here first.
 *
 * **Measured, and nothing did.** RAT-01 is the only rating service on the platform, its
 * `transactionType` is a one-member enum — `TRIP` — and it publishes a submit, a read by `ratingId`
 * and **no listing at all**, so trip ratings have no board either. This was a record that genuinely
 * did not exist, which [ADR 0090](../../../docs/adr/0090-a-support-rating-is-about-a-closed-case-and-nothing-held-one.md)
 * adds to SUP-01 — the service that owns the case, where the design's own precondition ("after a
 * case closes") is a row that already exists.
 *
 * ## Two of three columns are published and the first is a two-hop read
 *
 * | column  | source                                                                         |
 * | ------- | ------------------------------------------------------------------------------ |
 * | `Agent` | `agentSubject` → IAM-02 `workforce_members.displayName`, joined in this module |
 * | `Cases` | `ratedCases`, which is **rated** cases and not cases handled                   |
 * | `CSAT`  | `scoreTotal / ratedCases`, divided here because the service publishes no mean  |
 *
 * **`Agent` is the hop.** SUP-01 holds a Keycloak subject and no names; IAM-02's `workforce_members`
 * holds `displayName` keyed by `platformSubject`, and ADR 0084 published a listing of it. So this
 * board reads two operations and joins them, the way `Multi-stop in trip` reads a stop's place
 * through GEO-02. An agent with no workforce record is drawn as the subject with the reason —
 * never as a name this console invented.
 *
 * **`CSAT` is a division this module does and the service deliberately did not.** Publishing `4.8`
 * would be publishing a figure a reader cannot check; `142` and `683` are two integers whose
 * quotient they can. One decimal place, because that is what the design draws, and the panel carries
 * both integers so the rounding is answerable.
 *
 * ## None of the four tiles is drawn, and they are four different absences
 *
 * `CSAT this week · 4.6 / 5` is a mean over a time window, and the listing is not windowed — adding
 * one would mean a `from`/`to` on the projection, which is a change to ADR 0090 rather than to this
 * screen. `Responses · 1,204` is a count over a bounded listing (R-04). `Reopened · 3.1%` is a rate
 * over **cases**, not ratings: SUP-01's transitions allow `resolved → open`, so a reopen is a thing
 * that happens and nothing counts it. `Median first reply · 4 min` needs a first-reply instant, and
 * SUP-03's timeline holds entries while nothing marks which one is the first reply.
 *
 * ## The design's two badge words cannot be derived, and are not
 *
 * `done · top` and `caution · coaching` are a judgement about an agent's performance — somebody's
 * threshold, not a published field. Drawing them from a score would mean this console deciding what
 * `coaching` means for every tenant on the platform, which is the refusal the branches board makes
 * about a busy mode and the reports board makes about a slug. So every row carries the same neutral
 * state and the board says why under the grid.
 */

/** The most rows one request answers, which is this listing's own maximum. */
export const PER_FETCH = 200;

/**
 * The scope this board spends, which is the one the page has always declared.
 *
 * `support-satisfaction:read`, and the constant is here rather than only in `navigation.ts` so the
 * board's own module states what it spends. It is **not** `support-cases:read` and not a subset of
 * it: this listing answers no case at all, only sums per agent, so a console holding it learns how
 * agents are rated and nothing about any customer's case. ADR 0032.
 */
export const SATISFACTION_SCOPE = "support-satisfaction:read";

/** The scope the second hop spends — IAM-02's member listing, published by ADR 0084. */
export const MEMBERS_SCOPE = "workforce:member:read";

/** One row of the board: the projection, plus whatever the second hop could name. */
export interface AgentRow {
  readonly agentSubject: string;
  readonly ratedCases: number;
  readonly scoreTotal: number;
  readonly latestRatingAt: string;
  /** The display name IAM-02 holds for this subject, or undefined where it holds none. */
  readonly displayName?: string;
  /** The role IAM-02 holds, which the design draws no column for and the panel does. */
  readonly role?: string;
}

/**
 * Join the projection to the names, and never invent one.
 *
 * `platformSubject` is `UNIQUE (tenant_id, platform_subject)` on `workforce_members`, so the join is
 * one-to-one where it matches at all. An invited member has no subject yet and therefore matches
 * nothing, which is correct: somebody who has never signed in has handled no case.
 *
 * A subject with no member record keeps `displayName` undefined rather than falling back to
 * anything. The honest reasons it happens are worth knowing apart: the agent may be a platform
 * operator rather than tenant workforce, or the record may have been written before ADR 0084. Both
 * are cases where the board must show the subject and say so.
 */
export function agentRows(
  agents: readonly AgentSatisfaction[],
  members: readonly WorkforceMember[],
): readonly AgentRow[] {
  const bySubject = new Map<string, WorkforceMember>();
  for (const member of members) {
    if (member.platformSubject !== undefined && member.platformSubject !== null)
      bySubject.set(member.platformSubject, member);
  }
  return agents.map((agent) => {
    const member = bySubject.get(agent.agentSubject);
    return {
      agentSubject: agent.agentSubject,
      ratedCases: agent.ratedCases,
      scoreTotal: agent.scoreTotal,
      latestRatingAt: agent.latestRatingAt,
      ...(member === undefined ? {} : { displayName: member.displayName, role: member.role }),
    };
  });
}

/** Why a row shows a subject where the design draws a name. */
export const NO_MEMBER_RECORD =
  "IAM-02 holds no workforce member with this platform subject, so there is no name to show. This agent may be a platform operator rather than this tenant's workforce, or the record may predate ADR 0084. The subject is shown rather than a name this console made up.";

/**
 * The design's `Agent` cell: a name where IAM-02 has one, the subject where it does not.
 *
 * Monospaced in the second case, because a Keycloak subject is an identifier and the design sets
 * identifiers in Geist Mono. A name is not.
 */
export function agentCell(row: AgentRow): { text: string; mono: boolean; title?: string } {
  return row.displayName === undefined
    ? { text: row.agentSubject, mono: true, title: NO_MEMBER_RECORD }
    : { text: row.displayName, mono: false };
}

/**
 * The design's `CSAT` cell: one decimal place, which is a rounding this board states.
 *
 * `4.8`, as the design writes it. The division is exact and the display is not, so the panel carries
 * `ratedCases` and `scoreTotal` and the reader can reconstruct the figure — which is the whole
 * reason ADR 0090 publishes two integers and no mean.
 *
 * A row with no ratings cannot reach this: the projection is a `GROUP BY` over ratings, so every row
 * it answers has at least one. The guard is here anyway, because a zero divisor that reaches a
 * screen draws `NaN`, and `NaN` on a board somebody is assessed by is worse than a refusal.
 */
export function scoreCell(row: AgentRow): string {
  if (row.ratedCases <= 0) return "—";
  return (row.scoreTotal / row.ratedCases).toFixed(1);
}

/** The exact mean, unrounded, for the panel — so the rounded cell above is answerable. */
export function exactScore(row: AgentRow): string {
  if (row.ratedCases <= 0) return "—";
  return `${String(row.scoreTotal)} ÷ ${String(row.ratedCases)}`;
}

/**
 * The state every row carries, which is the same for all of them.
 *
 * The design's `top` and `coaching` are a judgement about performance against somebody's threshold,
 * and nothing publishes one. Drawing them from the score would mean this console deciding what
 * `coaching` means for every tenant on the platform — the refusal the branches board makes about a
 * busy mode and the reports board makes about a slug turned into a title.
 *
 * So the badge says the one thing that **is** published: that these are ratings of closed cases.
 * `StateBadge` takes the owning service and the contract's own word, and `rated` is the word for
 * what a row is.
 */
export function stateOf(row: AgentRow): {
  classKey: StateClassKey;
  word: string;
  title: string;
} {
  return {
    classKey: "done",
    word: "rated",
    title: `SUP-01 publishes ${String(row.ratedCases)} rated ${
      row.ratedCases === 1 ? "case" : "cases"
    } for this agent. The design's top and coaching badges are a judgement against a threshold nothing on this platform publishes, so no row claims either.`,
  };
}

/** The row detail panel: the two integers behind the cell, and the three absences. */
export function agentDetail(row: AgentRow): RecordDetail {
  const state = stateOf(row);
  return {
    headline: row.displayName ?? row.agentSubject,
    identifier: row.agentSubject,
    source: "SUP-01 · IAM-02",
    state: { classKey: state.classKey, label: state.word, code: "SUP-01", status: "rated" },
    fields: [
      field("Rated cases", String(row.ratedCases)),
      field("Score total", String(row.scoreTotal)),
      field("CSAT", `${scoreCell(row)} (${exactScore(row)})`),
      field("Latest rating", row.latestRatingAt, true),
      ...(row.role === undefined
        ? [
            absent(
              "Role",
              "IAM-02 holds no workforce member with this platform subject, so there is no role to show.",
            ),
          ]
        : [field("Role", row.role)]),
      absent(
        "Cases handled",
        "SUP-01 counts rated cases, which is not the same as cases handled — nothing counts the cases a customer chose not to rate. SUP-02 holds assignments and publishes no per-agent total.",
      ),
      absent(
        "Reopened",
        "Nothing counts a reopen. SUP-01's transitions allow resolved → open, so it is a thing that happens, and no service keeps the count the design's Reopened tile draws.",
      ),
      absent(
        "Median first reply",
        "Nothing publishes a first-reply instant. SUP-03 holds timeline entries and marks none of them as the first reply.",
      ),
    ],
  };
}

/** The design's own closing note, kept word for word. */
export const BOARD_NOTE = "Ratings are collected after a case closes, once per case.";

/**
 * The sentence under the grid, which carries what a reader is looking at and cannot see.
 *
 * One sentence rather than four tooltips: a reader seeing three columns where the design draws four
 * tiles and two badge words needs one explanation rather than a hunt.
 */
export const LISTING_NOTE =
  "Every agent with at least one rated case, in the order the service answered — by platform subject, which is the only ordering a grouped listing can page over without repeating or skipping a row. The design's sample is highest-score-first, and sorting by score here would disagree with that cursor. Cases counts rated cases rather than cases handled — nothing counts the cases a customer chose not to rate. The design's top and coaching badges are a judgement against a threshold nothing publishes, so no row claims either, and its four tiles are not drawn: a weekly mean needs a windowed listing, a reopen rate needs something that counts reopens, and a median first reply needs a first-reply instant nothing marks.";

/**
 * Why the design's one command is drawn and not sent.
 *
 * `Export satisfaction ratings` — From, To, Format. **The format half is real and the range half is
 * not.** This console exports what it holds, to CSV, Excel and PDF, which `export.ts` does for every
 * board; what it cannot do is ask the service for a date range, because ADR 0090's listing is not
 * windowed. So the export writes the board, and the form's two date fields have nothing to send.
 */
export const EXPORT_RANGE_REFUSAL =
  "The export writes what this board holds. Its From and To have nowhere to go: SUP-01's satisfaction listing takes a page and a cursor and no date range, so a range asked for here would be applied to the page on screen and read as though it had been applied to the tenant.";

/* ------------------------------------------------------- the board's own filtering and ordering */

/**
 * The design's four orders, imported rather than invented.
 *
 * **This module first declared its own `"score" | "cases"` pair, and that was a fidelity failure
 * rather than a convenience.** The design's toolbar offers exactly four options — `Default order`,
 * `State`, `Name A–Z`, `Name Z–A` — and CLAUDE.md section 1 says not to add anything that is not
 * in the design. A board offering two sorts the approved page does not is a board a reviewer cannot
 * compare against it.
 *
 * The temptation was real, because the design's own sample rows are in **descending score** order —
 * `4.8`, `4.5`, `4.1` — so a score sort looks like what the page wants. What it actually shows is
 * that the design's default order is by score, and this listing's is by agent subject, because that
 * is the only immutable ordering a grouped projection has (ADR 0090 §3). That difference is said
 * under the grid rather than papered over with a control the design does not draw.
 */
export type AgentOrder = GridOrder;

export interface AgentQuery {
  readonly search: string;
  readonly order: AgentOrder;
  readonly page: number;
  readonly perPage: number;
}

export interface AgentView {
  readonly rows: readonly AgentRow[];
  /** Every row that matched, across all pages — what an export writes. */
  readonly all: readonly AgentRow[];
  readonly total: number;
  readonly pageCount: number;
}

/**
 * Filter, order and page what the screen holds.
 *
 * `default` is the order the service answered in, which is by agent subject — the grouping's own
 * ordering and the only immutable one it has (ADR 0090 §3). **The design's sample rows are in
 * descending score order and this board's default is not**, which is stated under the grid rather
 * than fixed with a sort the design's toolbar does not offer: a board that re-sorted every page by
 * score would disagree with its own cursor the moment a second page arrived, and the figures would
 * reshuffle under a reader who was comparing them.
 *
 * The search matches the name, the subject and the role, so an operator can find a person by
 * whichever of the three they have.
 */
export function agentPage(rows: readonly AgentRow[], query: AgentQuery): AgentView {
  const needle = query.search.trim().toLowerCase();
  const matched = rows.filter(
    (row) =>
      needle.length === 0 ||
      [row.displayName ?? "", row.agentSubject, row.role ?? ""]
        .join(" ")
        .toLowerCase()
        .includes(needle),
  );

  /*
   * `state` is offered and changes nothing, which is the honest thing for it to do.
   *
   * Every row on this board carries the same state — `rated` — because the design's `top` and
   * `coaching` are a judgement against a threshold nothing publishes. So sorting by state is a
   * stable no-op rather than a removed control, and the toolbar says there are no states to filter.
   * Removing the option would change the design's toolbar; making it reorder rows arbitrarily would
   * be worse.
   */
  const named = (row: AgentRow): string => row.displayName ?? row.agentSubject;
  const ordered =
    query.order === "default" || query.order === "state"
      ? matched
      : [...matched].sort((left, right) => {
          const compared = named(left).localeCompare(named(right));
          return query.order === "az" ? compared : -compared;
        });

  const total = ordered.length;
  const pageCount = Math.max(1, Math.ceil(total / query.perPage));
  const page = Math.min(Math.max(1, query.page), pageCount);
  const from = (page - 1) * query.perPage;

  return { rows: ordered.slice(from, from + query.perPage), all: ordered, total, pageCount };
}

/**
 * The sentence that says what was read, or nothing.
 *
 * Counts agents, which is what the listing answers one of. `truncated` is the service's own
 * `nextAfter` being non-null, so the sentence is a fact about the cursor rather than an inference
 * from the row count.
 */
export function fetchedNote(count: number | undefined, truncated: boolean): string | undefined {
  if (count === undefined) return undefined;
  const read = count === 1 ? "1 agent" : `${String(count)} agents`;
  return truncated ? `${read}, and there are more.` : `${read}, which is all of them.`;
}
